import { useEffect, useRef, type RefObject } from "react";

import { swipeDirection } from "@/lib/timeline/view/StageSwipe/swipe";
import { canStartTimelinePanDrag } from "@/lib/timeline/view/ViewTransform/transform";

/** With the camera locked, one finger flicks between scenes. Two fingers still pinch. */
export function useStageSwipe(
  stageRef: RefObject<HTMLElement | null>,
  locked: boolean,
  skipScene: (direction: -1 | 1) => void,
): void {
  const skip = useRef(skipScene);
  skip.current = skipScene;

  useEffect(() => {
    const stage = stageRef.current;
    if (stage === null || !locked) {
      return;
    }
    const fingers = new Set<number>();
    let start: { x: number; y: number; at: number } | null = null;
    const onDown = (event: PointerEvent): void => {
      if (event.pointerType !== "touch") {
        return;
      }
      fingers.add(event.pointerId);
      start = fingers.size === 1 && canStartTimelinePanDrag(event.target)
        ? { x: event.clientX, y: event.clientY, at: event.timeStamp }
        : null;
    };
    const onUp = (event: PointerEvent): void => {
      fingers.delete(event.pointerId);
      if (event.pointerType !== "touch" || start === null) {
        return;
      }
      const direction = swipeDirection({
        dx: event.clientX - start.x,
        dy: event.clientY - start.y,
        ms: event.timeStamp - start.at,
      });
      start = null;
      if (direction !== null) {
        navigator.vibrate?.(10);
        skip.current(direction);
      }
    };
    const onCancel = (event: PointerEvent): void => {
      fingers.delete(event.pointerId);
      start = null;
    };
    stage.addEventListener("pointerdown", onDown);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      stage.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [locked, stageRef]);
}
